/* eslint-disable no-nested-ternary */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { useAuthHttp } from '../../hooks';
import { useAuth } from '../../context';
import {
  ACCEPT_WAREHOUSE_MATCH,
  DELETE_WAREHOUSE_MATCH,
  GET_MATCH_SIGNING_DETAILS,
  MATCH_LIST,
  PROPOSE_WAREHOUSE_MATCH_DETAILS,
  SUPPORTED_CURRENCY_GROUPS,
  WAREHOUSE_GET,
} from '../../constants';
import { confirmationMessageAtom, matchAtom } from '../../store/jotai';
import { FormPage } from '../../tailwindComponents/FormPage';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Datepicker } from '../../tailwindComponents/Datepicker';
import { Textarea } from '../../tailwindComponents/Textarea';
import { Checkbox } from '../../tailwindComponents/Checkbox';
import { Button } from '../../tailwindComponents/Button';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { CommentThread } from '../../components/CommentThread';

// Types
type ServiceRate = {
  originalValue: number;
  shipperProposedValue: number;
  lspProposedValue: number;
  acceptedValue: number;
};

type Warehouse = {
  warehouseId: string;
  name: string;
  address: string;
  city: string;
  currency: string;
  storageCapacityUnitOfMeasure: string;
  palletStoragePricePerWeek: number;
  squareFeetStoragePricePerWeek: number;
  squareMetreStoragePricePerWeek: number;
  palletReceiptAndPutAwayPrice: number;
  palletPickingAndDespatchPrice: number;
  palletLayerPickingAndDespatchPrice: number;
  casePickingAndDespatchPrice: number;
  subsequentCasePickingAndDespatchPrice: number;
  itemPickingAndDespatchPrice: number;
  subsequentItemPickingAndDespatchPrice: number;
  containerHandlingPrice: number;
  demurragePricePerDay: number;
  palletReturnsPrice: number;
  itemReturnsPrice: number;
  caseReturnsPrice: number;
  auxiliaryServicePricePerManHour: number;
  userBusinessName: string;
  isPartialProfile: boolean;
};

type Match = {
  id: string;
  warehouseId: string;
  shipperId: string;
  desiredStartDate: string;
  averageStorageQuantityPerWeek: number;
  estimatedDurationInMonths: number;
  averageNumberOfInboundShipmentsPerWeek: number;
  averageNumberOfOutboundShipmentsPerWeek: number;
  shipperIndustry: string;
  requestedServices: string[];
  unavailableServices: string[];
  matchStatus: string;
  billingCycleFrequency: string;
  paymentTermsInDays: number;
  originalRates: {
    storagePricePerWeek: number;
    palletReceiptAndPutAwayPrice: number;
    palletPickingAndDespatchPrice: number;
    palletLayerPickingAndDespatchPrice: number;
    casePickingAndDespatchPrice: number;
    subsequentCasePickingAndDespatchPrice: number;
    itemPickingAndDespatchPrice: number;
    subsequentItemPickingAndDespatchPrice: number;
    containerHandlingPrice: number;
    demurragePricePerDay: number;
    palletReturnsPrice: number;
    itemReturnsPrice: number;
    caseReturnsPrice: number;
    auxiliaryServicePricePerManHour: number;
  };
  serviceRates: {
    storagePricePerWeek: ServiceRate;
    palletReceiptAndPutAwayPrice: ServiceRate;
    palletPickingAndDespatchPrice: ServiceRate;
    palletLayerPickingAndDespatchPrice: ServiceRate;
    casePickingAndDespatchPrice: ServiceRate;
    subsequentCasePickingAndDespatchPrice: ServiceRate;
    itemPickingAndDespatchPrice: ServiceRate;
    subsequentItemPickingAndDespatchPrice: ServiceRate;
    containerHandlingPrice: ServiceRate;
    demurragePricePerDay: ServiceRate;
    palletReturnsPrice: ServiceRate;
    itemReturnsPrice: ServiceRate;
    caseReturnsPrice: ServiceRate;
    auxiliaryServicePricePerManHour: ServiceRate;
  };
  volumeDiscounts: {
    id: string;
    acceptedValue: number;
    lspProposedDiscountPercentage: number;
    minTotalMonthlyTurnover: number;
    maxTotalMonthlyTurnover: number;
    shipperProposedDiscountPercentage: number;
  }[];
  isActionRequiredByLsp: boolean;
  isActionRequiredByShipper: boolean;
  comments: {
    commentText: string;
    createdAt: string;
    createdByUserId: string;
    createdByUserName: string;
  }[];
  userBusinessName: string;
  legalDocuments: {
    documentId: string;
    createdAt: string;
    updatedAt: string;
    slug: string;
    documentName: string;
    name: string;
    completedAt: string;
    status: string;
    url: string;
  }[];
  floxTransactionFees: {
    storageFeePercentage: number;
    handlingFeePercentage: number;
    haulageFeePercentage: number;
    distributionFeePercentage: number;
    otherServicesPercentage: number;
    financialTransactionFeePercentage: number;
    discountPercentage: number;
  };
  matchParties: {
    userId: string;
    userBusinessId: string;
    emailAddress: string;
    isNegotiating: boolean;
    hasAccepted: boolean;
    isSigningDocument: boolean;
    name: string;
    roles: string[];
    companyNumber: string;
    companyName: string;
    companyAddress: string;
  }[];
};

export type ManagerServiceRatesFormValues = {
  storagePricePerWeek: number | null;
  storagePricePerWeekUnavailable: boolean;
  palletReceiptAndPutAwayPrice: number | null;
  palletReceiptAndPutAwayPriceUnavailable: boolean;
  palletPickingAndDespatchPrice: number | null;
  palletPickingAndDespatchPriceUnavailable: boolean;
  palletLayerPickingAndDespatchPrice: number | null;
  palletLayerPickingAndDespatchPriceUnavailable: boolean;
  casePickingAndDespatchPrice: number | null;
  casePickingAndDespatchPriceUnavailable: boolean;
  subsequentCasePickingAndDespatchPrice: number | null;
  subsequentCasePickingAndDespatchPriceUnavailable: boolean;
  itemPickingAndDespatchPrice: number | null;
  itemPickingAndDespatchPriceUnavailable: boolean;
  subsequentItemPickingAndDespatchPrice: number | null;
  subsequentItemPickingAndDespatchPriceUnavailable: boolean;
  containerHandlingPrice: number | null;
  containerHandlingPriceUnavailable: boolean;
  demurragePricePerDay: number | null;
  demurragePricePerDayUnavailable: boolean;
  palletReturnsPrice: number | null;
  palletReturnsPriceUnavailable: boolean;
  itemReturnsPrice: number | null;
  itemReturnsPriceUnavailable: boolean;
  caseReturnsPrice: number | null;
  caseReturnsPriceUnavailable: boolean;
  auxiliaryServicePricePerManHour: number | null;
  auxiliaryServicePricePerManHourUnavailable: boolean;
  unavailableServices: string[];
  volumeBasedDiscount: boolean;
  monthlyTurnoverOne: number;
  monthlyDiscountOne: number;
  monthlyTurnoverTwo: number;
  monthlyDiscountTwo: number;
  monthlyTurnoverThree: number;
  monthlyDiscountThree: number;
  billingCycleFrequency: string;
  paymentTermsInDays: number;
  desiredStartDate: string;
  comment: string;
};

const BILLING_CYCLE_OPTIONS = [
  { label: 'Weekly', value: 'Weekly' },
  { label: 'Every Two Weeks', value: 'Every Two Weeks' },
  { label: 'Monthly', value: 'Monthly' },
];

const PAYMENT_TERMS_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '14 days', value: 14 },
  { label: '30 days', value: 30 },
];

const validationSchema = Yup.object().shape({
  storagePricePerWeek: Yup.number()
    .nullable()
    .when(
      ['volumeBasedDiscount', 'storagePricePerWeekUnavailable'],
      ([volumeBasedDiscount, unavailable], schema) =>
        volumeBasedDiscount || unavailable
          ? schema.nullable()
          : schema.required(
              'A storage price is required — set a price or mark it as N/A.'
            )
    ),
  palletReceiptAndPutAwayPrice: Yup.number()
    .nullable()
    .when(
      ['volumeBasedDiscount', 'palletReceiptAndPutAwayPriceUnavailable'],
      ([volumeBasedDiscount, unavailable], schema) =>
        volumeBasedDiscount || unavailable
          ? schema.nullable()
          : schema.required(
              'A receipt and putaway price is required — set a price or mark it as N/A.'
            )
    ),
  desiredStartDate: Yup.string().required('A desired start date is required.'),
  billingCycleFrequency: Yup.string().required('Billing cycle is required'),
  paymentTermsInDays: Yup.number().required('Payment terms are required'),
});

// Service rate row component for manager - includes unavailable checkbox
type ManagerServiceRateRowProps = {
  label: string;
  fieldName: string;
  warehousePrice: number | null;
  currencySymbol: string | undefined;
  match: Match | null;
  control: ReturnType<typeof useForm<ManagerServiceRatesFormValues>>['control'];
  watch: ReturnType<typeof useForm<ManagerServiceRatesFormValues>>['watch'];
  setValue: ReturnType<
    typeof useForm<ManagerServiceRatesFormValues>
  >['setValue'];
  volumeBasedDiscount: boolean;
  unavailableServices: string[];
};

function ManagerServiceRateRow({
  label,
  fieldName,
  warehousePrice,
  currencySymbol,
  match,
  control,
  watch,
  setValue,
  volumeBasedDiscount,
  unavailableServices,
}: ManagerServiceRateRowProps) {
  const shipperProposedValue =
    match?.serviceRates?.[fieldName as keyof typeof match.serviceRates]
      ?.shipperProposedValue;
  const acceptedValue =
    match?.serviceRates?.[fieldName as keyof typeof match.serviceRates]
      ?.acceptedValue;
  const isUnavailable = watch(
    `${fieldName}Unavailable` as keyof ManagerServiceRatesFormValues
  );
  const isRequested = match?.requestedServices?.includes(fieldName);
  const hasNoPrice = warehousePrice === null && !acceptedValue;

  const displayValue = acceptedValue ?? warehousePrice;

  return (
    <div
      className={`grid grid-cols-12 gap-4 py-3 px-4 border-b border-gray-100 hover:bg-gray-50/50 transition-colors ${
        isRequested && hasNoPrice && !isUnavailable
          ? 'bg-amber-50/70 border-l-4 border-l-amber-400'
          : ''
      }`}
    >
      {/* Service Name */}
      <div className='col-span-3 flex items-center gap-2'>
        <span className='text-sm text-gray-700'>{label}</span>
        {isRequested && hasNoPrice && !isUnavailable && (
          <span className='text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium'>
            Requested
          </span>
        )}
      </div>

      {/* Original Rate */}
      <div className='col-span-2 flex items-center'>
        {isUnavailable ? (
          <span className='text-sm text-gray-400 italic'>N/A</span>
        ) : displayValue ? (
          <span className='text-sm font-semibold text-gray-900'>
            {currencySymbol}
            {displayValue.toLocaleString()}
          </span>
        ) : isRequested ? (
          <span className='text-xs text-amber-600 font-medium'>
            Price needed
          </span>
        ) : (
          <span className='text-sm text-gray-400'>—</span>
        )}
      </div>

      {/* Shipper Proposed */}
      <div className='col-span-2 flex items-center'>
        {shipperProposedValue != null && !isUnavailable ? (
          <span
            className={`text-sm font-medium ${
              shipperProposedValue !== warehousePrice
                ? 'text-amber-600'
                : 'text-gray-900'
            }`}
          >
            {currencySymbol}
            {shipperProposedValue.toLocaleString()}
          </span>
        ) : (
          <span className='text-sm text-gray-400'>—</span>
        )}
      </div>

      {/* Your Counter */}
      <div className='col-span-3'>
        {!isUnavailable && (
          <Controller
            name={fieldName as keyof ManagerServiceRatesFormValues}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                name={fieldName}
                type='number'
                placeholder={
                  warehousePrice ? String(warehousePrice) : 'Set price'
                }
                value={field.value?.toString() ?? ''}
                onChange={(e) =>
                  field.onChange(e.target.value ? Number(e.target.value) : null)
                }
                disabled={
                  volumeBasedDiscount || match?.isActionRequiredByShipper
                }
                isInvalid={!!error}
                validationMessage={error?.message}
              />
            )}
          />
        )}
      </div>

      {/* Unavailable Checkbox */}
      <div className='col-span-2 flex items-center justify-center'>
        <Controller
          name={
            `${fieldName}Unavailable` as keyof ManagerServiceRatesFormValues
          }
          control={control}
          render={({ field }) => (
            <input
              type='checkbox'
              checked={!!field.value}
              onChange={(e) => {
                if (match?.isActionRequiredByLsp) {
                  field.onChange(e.target.checked);
                  if (e.target.checked) {
                    setValue(
                      fieldName as keyof ManagerServiceRatesFormValues,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      null as any
                    );
                    setValue('unavailableServices', [
                      ...unavailableServices,
                      fieldName,
                    ]);
                  } else {
                    setValue(
                      fieldName as keyof ManagerServiceRatesFormValues,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (shipperProposedValue ?? warehousePrice) as any
                    );
                    setValue(
                      'unavailableServices',
                      unavailableServices.filter((s) => s !== fieldName)
                    );
                  }
                }
              }}
              disabled={match?.isActionRequiredByShipper}
              className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
            />
          )}
        />
      </div>
    </div>
  );
}

export function ManagerServiceRates() {
  const { warehouseId } = useParams<{ warehouseId: string }>();
  const [searchParams] = useSearchParams();
  const matchId = searchParams.get('matchId');

  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const { user } = useAuth();

  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [match, setMatch] = useAtom(matchAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [, setConfirmationMessage] = useAtom(confirmationMessageAtom);
  const [matchSigningDetails, setMatchSigningDetails] = useState<{
    isSigned: boolean;
    signedAt: string;
    signedByEmail: string;
    signingUrl: string;
  } | null>(null);
  const [signingDetailsError, setSigningDetailsError] = useState<string | null>(
    null
  );

  const currencies = SUPPORTED_CURRENCY_GROUPS.flatMap(
    (currencyGroup) => currencyGroup.currencies
  );

  const currencySymbol = warehouse?.currency
    ? currencies.find((currency) => currency.code === warehouse.currency)
        ?.symbol
    : '£';

  const steps = ['Service Rates', 'Terms & Commitment'];

  const pages = [
    { name: 'Dashboard', href: '/dashboard', current: false },
    {
      name: warehouse?.name || 'Warehouse',
      href: `/warehouse/${warehouseId}`,
      current: false,
    },
    { name: 'Negotiate Rates', href: '#', current: true },
  ];

  const defaultValues: ManagerServiceRatesFormValues = {
    storagePricePerWeek: null,
    storagePricePerWeekUnavailable: false,
    palletReceiptAndPutAwayPrice: null,
    palletReceiptAndPutAwayPriceUnavailable: false,
    palletPickingAndDespatchPrice: null,
    palletPickingAndDespatchPriceUnavailable: false,
    palletLayerPickingAndDespatchPrice: null,
    palletLayerPickingAndDespatchPriceUnavailable: false,
    casePickingAndDespatchPrice: null,
    casePickingAndDespatchPriceUnavailable: false,
    subsequentCasePickingAndDespatchPrice: null,
    subsequentCasePickingAndDespatchPriceUnavailable: false,
    itemPickingAndDespatchPrice: null,
    itemPickingAndDespatchPriceUnavailable: false,
    subsequentItemPickingAndDespatchPrice: null,
    subsequentItemPickingAndDespatchPriceUnavailable: false,
    containerHandlingPrice: null,
    containerHandlingPriceUnavailable: false,
    demurragePricePerDay: null,
    demurragePricePerDayUnavailable: false,
    palletReturnsPrice: null,
    palletReturnsPriceUnavailable: false,
    itemReturnsPrice: null,
    itemReturnsPriceUnavailable: false,
    caseReturnsPrice: null,
    caseReturnsPriceUnavailable: false,
    auxiliaryServicePricePerManHour: null,
    auxiliaryServicePricePerManHourUnavailable: false,
    unavailableServices: [],
    volumeBasedDiscount: false,
    monthlyTurnoverOne: 0,
    monthlyDiscountOne: 0,
    monthlyTurnoverTwo: 0,
    monthlyDiscountTwo: 0,
    monthlyTurnoverThree: 0,
    monthlyDiscountThree: 0,
    billingCycleFrequency: 'Monthly',
    paymentTermsInDays: 30,
    desiredStartDate: dayjs().format('YYYY-MM-DD'),
    comment: '',
  };

  const methods = useForm<ManagerServiceRatesFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(validationSchema) as any,
    mode: 'onChange',
    defaultValues,
  });

  const { control, watch, reset, setValue } = methods;

  const volumeBasedDiscount = watch('volumeBasedDiscount');
  const unavailableServices = watch('unavailableServices');

  // Common props for ManagerServiceRateRow components
  const serviceRateRowCommonProps = {
    currencySymbol,
    match,
    control,
    watch,
    setValue,
    volumeBasedDiscount,
    unavailableServices,
  };

  // Fetch warehouse data
  const fetchWarehouse = useCallback(
    async (isStillThisNegotiation: () => boolean) => {
      if (!warehouseId) return;

      try {
        const { data } = await authHttp.get(WAREHOUSE_GET, {
          params: { warehouseId },
        });
        if (data?.warehouse && isStillThisNegotiation()) {
          setWarehouse(data.warehouse);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching warehouse:', error);
      }
    },
    [warehouseId, authHttp]
  );

  // Fetch match data
  const fetchMatch = useCallback(
    async (isStillThisNegotiation: () => boolean) => {
      if (!warehouseId) return;

      try {
        const { data } = await authHttp.post(
          MATCH_LIST,
          {
            warehouseId,
            matchId: matchId || null,
          },
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (data?.matches && data.matches.length > 0) {
          const targetMatch = matchId
            ? data.matches.find(
                (m: { match: { id: string } }) => m.match.id === matchId
              )
            : data.matches.find(
                (m: { match: { matchStatus: string } }) =>
                  m.match.matchStatus !== 'Rejected'
              );
          if (targetMatch && isStillThisNegotiation()) {
            setMatch(targetMatch.match);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching match:', error);
      }
    },
    [warehouseId, matchId, authHttp, setMatch]
  );

  // Fetch signing details
  const fetchSigningDetails = useCallback(
    async (isStillThisMatch: () => boolean) => {
      if (!match?.id) return;

      try {
        const { data } = await authHttp.get(GET_MATCH_SIGNING_DETAILS, {
          params: { matchId: match.id },
        });
        if (data && isStillThisMatch()) {
          setMatchSigningDetails(data);
          setSigningDetailsError(null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching signing details:', error);
        if (!isStillThisMatch()) return;
        const backendError = (
          error as {
            response?: { data?: { errors?: { errorMessage?: string }[] } };
          }
        ).response?.data?.errors?.[0]?.errorMessage;
        setSigningDetailsError(
          backendError ??
            'We could not load your contract for signing. Please try again, or contact support if the problem continues.'
        );
      }
    },
    [match?.id, authHttp]
  );

  // Initial data fetch
  useEffect(() => {
    let isStillThisNegotiation = true;

    const loadData = async () => {
      setIsLoading(true);
      setWarehouse(null);
      setMatch(null);
      setMatchSigningDetails(null);
      setSigningDetailsError(null);
      setSubmitError(null);
      setCurrentStep(1);
      await Promise.all([
        fetchWarehouse(() => isStillThisNegotiation),
        fetchMatch(() => isStillThisNegotiation),
      ]);
      if (isStillThisNegotiation) setIsLoading(false);
    };
    loadData();
    return () => {
      isStillThisNegotiation = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, matchId]);

  // Fetch signing details when match is loaded.
  // Keyed on stable values only — fetchSigningDetails changes identity every
  // render (useAuthHttp returns a fresh instance), so depending on it refires
  // the effect after each fetch and loops forever.
  useEffect(() => {
    let isStillThisMatch = true;
    if (
      match?.matchStatus === 'Reviewing T&Cs' ||
      match?.matchStatus === 'Contracted'
    ) {
      fetchSigningDetails(() => isStillThisMatch);
    }
    return () => {
      isStillThisMatch = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.matchStatus, match?.id]);

  // Populate form with match data
  useEffect(() => {
    if (match && warehouse) {
      const getServiceRateValue = (key: keyof typeof match.originalRates) => {
        if (match.unavailableServices?.includes(key)) {
          return null;
        }
        if (match.isActionRequiredByLsp) {
          return (
            match.serviceRates?.[key]?.shipperProposedValue ??
            match.serviceRates?.[key]?.lspProposedValue ??
            match.originalRates[key]
          );
        }
        return (
          match.serviceRates?.[key]?.lspProposedValue ??
          match.originalRates[key]
        );
      };

      const desiredStartDate =
        match.desiredStartDate === null ||
        dayjs(match.desiredStartDate).isBefore(dayjs())
          ? dayjs().format('YYYY-MM-DD')
          : dayjs(match.desiredStartDate).format('YYYY-MM-DD');

      reset({
        storagePricePerWeek: getServiceRateValue('storagePricePerWeek'),
        storagePricePerWeekUnavailable: match.unavailableServices?.includes(
          'storagePricePerWeek'
        ),
        palletReceiptAndPutAwayPrice: getServiceRateValue(
          'palletReceiptAndPutAwayPrice'
        ),
        palletReceiptAndPutAwayPriceUnavailable:
          match.unavailableServices?.includes('palletReceiptAndPutAwayPrice'),
        palletPickingAndDespatchPrice: getServiceRateValue(
          'palletPickingAndDespatchPrice'
        ),
        palletPickingAndDespatchPriceUnavailable:
          match.unavailableServices?.includes('palletPickingAndDespatchPrice'),
        palletLayerPickingAndDespatchPrice: getServiceRateValue(
          'palletLayerPickingAndDespatchPrice'
        ),
        palletLayerPickingAndDespatchPriceUnavailable:
          match.unavailableServices?.includes(
            'palletLayerPickingAndDespatchPrice'
          ),
        casePickingAndDespatchPrice: getServiceRateValue(
          'casePickingAndDespatchPrice'
        ),
        casePickingAndDespatchPriceUnavailable:
          match.unavailableServices?.includes('casePickingAndDespatchPrice'),
        subsequentCasePickingAndDespatchPrice: getServiceRateValue(
          'subsequentCasePickingAndDespatchPrice'
        ),
        subsequentCasePickingAndDespatchPriceUnavailable:
          match.unavailableServices?.includes(
            'subsequentCasePickingAndDespatchPrice'
          ),
        itemPickingAndDespatchPrice: getServiceRateValue(
          'itemPickingAndDespatchPrice'
        ),
        itemPickingAndDespatchPriceUnavailable:
          match.unavailableServices?.includes('itemPickingAndDespatchPrice'),
        subsequentItemPickingAndDespatchPrice: getServiceRateValue(
          'subsequentItemPickingAndDespatchPrice'
        ),
        subsequentItemPickingAndDespatchPriceUnavailable:
          match.unavailableServices?.includes(
            'subsequentItemPickingAndDespatchPrice'
          ),
        containerHandlingPrice: getServiceRateValue('containerHandlingPrice'),
        containerHandlingPriceUnavailable: match.unavailableServices?.includes(
          'containerHandlingPrice'
        ),
        demurragePricePerDay: getServiceRateValue('demurragePricePerDay'),
        demurragePricePerDayUnavailable: match.unavailableServices?.includes(
          'demurragePricePerDay'
        ),
        palletReturnsPrice: getServiceRateValue('palletReturnsPrice'),
        palletReturnsPriceUnavailable:
          match.unavailableServices?.includes('palletReturnsPrice'),
        itemReturnsPrice: getServiceRateValue('itemReturnsPrice'),
        itemReturnsPriceUnavailable:
          match.unavailableServices?.includes('itemReturnsPrice'),
        caseReturnsPrice: getServiceRateValue('caseReturnsPrice'),
        caseReturnsPriceUnavailable:
          match.unavailableServices?.includes('caseReturnsPrice'),
        auxiliaryServicePricePerManHour: getServiceRateValue(
          'auxiliaryServicePricePerManHour'
        ),
        auxiliaryServicePricePerManHourUnavailable:
          match.unavailableServices?.includes(
            'auxiliaryServicePricePerManHour'
          ),
        unavailableServices: match.unavailableServices ?? [],
        volumeBasedDiscount: match.volumeDiscounts?.length > 0,
        monthlyTurnoverOne:
          match.volumeDiscounts?.[0]?.maxTotalMonthlyTurnover || 0,
        monthlyDiscountOne: match.isActionRequiredByLsp
          ? match.volumeDiscounts?.[0]?.shipperProposedDiscountPercentage || 0
          : match.volumeDiscounts?.[0]?.lspProposedDiscountPercentage || 0,
        monthlyTurnoverTwo:
          match.volumeDiscounts?.[1]?.maxTotalMonthlyTurnover || 0,
        monthlyDiscountTwo: match.isActionRequiredByLsp
          ? match.volumeDiscounts?.[1]?.shipperProposedDiscountPercentage || 0
          : match.volumeDiscounts?.[1]?.lspProposedDiscountPercentage || 0,
        monthlyTurnoverThree:
          match.volumeDiscounts?.[2]?.maxTotalMonthlyTurnover || 0,
        monthlyDiscountThree: match.isActionRequiredByLsp
          ? match.volumeDiscounts?.[2]?.shipperProposedDiscountPercentage || 0
          : match.volumeDiscounts?.[2]?.lspProposedDiscountPercentage || 0,
        billingCycleFrequency: match.billingCycleFrequency || 'Monthly',
        paymentTermsInDays: match.paymentTermsInDays || 30,
        desiredStartDate,
        comment: '',
      });
    }
  }, [match, warehouse, reset]);

  // Submit counter-proposal handler
  const onSubmit = async (data: ManagerServiceRatesFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const { data: proposal } = await authHttp.post(
        PROPOSE_WAREHOUSE_MATCH_DETAILS,
        {
          match: {
            matchId: match?.id,
            desiredStartDate: dayjs(data.desiredStartDate).format('YYYY-MM-DD'),
            averageStorageQuantityPerWeek: match?.averageStorageQuantityPerWeek,
            estimatedDurationInMonths: match?.estimatedDurationInMonths,
            averageNumberOfInboundShipmentsPerWeek:
              match?.averageNumberOfInboundShipmentsPerWeek,
            averageNumberOfOutboundShipmentsPerWeek:
              match?.averageNumberOfOutboundShipmentsPerWeek,
            shipperIndustry: match?.shipperIndustry || '',
            requestedServices: null,
            unavailableServices: data.unavailableServices,
            serviceRates: data.volumeBasedDiscount
              ? null
              : {
                  storagePricePerWeek: data.storagePricePerWeek,
                  palletReceiptAndPutAwayPrice:
                    data.palletReceiptAndPutAwayPrice,
                  palletPickingAndDespatchPrice:
                    data.palletPickingAndDespatchPrice,
                  palletLayerPickingAndDespatchPrice:
                    data.palletLayerPickingAndDespatchPrice,
                  casePickingAndDespatchPrice: data.casePickingAndDespatchPrice,
                  subsequentCasePickingAndDespatchPrice:
                    data.subsequentCasePickingAndDespatchPrice,
                  itemPickingAndDespatchPrice: data.itemPickingAndDespatchPrice,
                  subsequentItemPickingAndDespatchPrice:
                    data.subsequentItemPickingAndDespatchPrice,
                  containerHandlingPrice: data.containerHandlingPrice,
                  demurragePricePerDay: data.demurragePricePerDay,
                  palletReturnsPrice: data.palletReturnsPrice,
                  itemReturnsPrice: data.itemReturnsPrice,
                  caseReturnsPrice: data.caseReturnsPrice,
                  auxiliaryServicePricePerManHour:
                    data.auxiliaryServicePricePerManHour,
                },
            volumeDiscounts: data.volumeBasedDiscount
              ? [
                  {
                    id: match?.volumeDiscounts?.[0]?.id,
                    maxTotalMonthlyTurnover: data.monthlyTurnoverOne,
                    minTotalMonthlyTurnover: 0,
                    discountPercentage: data.monthlyDiscountOne,
                  },
                  ...(data.monthlyTurnoverTwo && data.monthlyDiscountTwo
                    ? [
                        {
                          id: match?.volumeDiscounts?.[1]?.id,
                          maxTotalMonthlyTurnover: data.monthlyTurnoverTwo,
                          minTotalMonthlyTurnover: data.monthlyTurnoverOne + 1,
                          discountPercentage: data.monthlyDiscountTwo,
                        },
                      ]
                    : []),
                  ...(data.monthlyTurnoverTwo && data.monthlyDiscountThree
                    ? [
                        {
                          id: match?.volumeDiscounts?.[2]?.id,
                          minTotalMonthlyTurnover: data.monthlyTurnoverTwo + 1,
                          discountPercentage: data.monthlyDiscountThree,
                        },
                      ]
                    : []),
                ]
              : null,
            billingCycleFrequency: data.billingCycleFrequency,
            paymentTermsInDays: data.paymentTermsInDays,
            comment: data.comment,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (proposal) {
        setMatch(proposal.match);
        setConfirmationMessage('Counter-proposal submitted successfully');
        navigate('/customers');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting proposal:', error);
      const backendError = (
        error as {
          response?: { data?: { errors?: { errorMessage?: string }[] } };
        }
      ).response?.data?.errors?.[0]?.errorMessage;
      setSubmitError(
        backendError ??
          'Something went wrong submitting your proposal. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Accept match handler
  const handleAcceptMatch = async () => {
    setIsAccepting(true);
    setSubmitError(null);
    try {
      const { data: proposal } = await authHttp.post(
        ACCEPT_WAREHOUSE_MATCH,
        {
          match: { matchId: match?.id },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (proposal) {
        setMatch(proposal.match);
        setConfirmationMessage('Rates accepted successfully');
        navigate('/customers');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error accepting match:', error);
      const backendError = (
        error as {
          response?: { data?: { errors?: { errorMessage?: string }[] } };
        }
      ).response?.data?.errors?.[0]?.errorMessage;
      setSubmitError(
        backendError ??
          'Something went wrong accepting the rates. Please try again.'
      );
    } finally {
      setIsAccepting(false);
    }
  };

  // Delete/Cancel match handler
  const handleDeleteMatch = async () => {
    if (!match?.id) return;

    setIsDeleting(true);
    try {
      await authHttp.post(
        DELETE_WAREHOUSE_MATCH,
        {
          match: {
            matchId: match.id,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setConfirmationMessage('Negotiation cancelled');
      navigate(`/warehouse/${warehouseId}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting match:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='flex flex-col items-center gap-3'>
          <FloxSpinner size='md' />
          <p className='text-gray-500 text-sm'>Loading service rates...</p>
        </div>
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <p className='text-gray-500'>Warehouse not found</p>
      </div>
    );
  }

  if (!match) {
    return (
      <div className='h-[calc(100vh-64px)] flex flex-col bg-gray-50'>
        <PageHeader header='Negotiate Rates' pages={pages} />
        <div className='flex-1 flex flex-col items-center justify-center px-10 py-6'>
          <div className='max-w-md text-center bg-white rounded-xl shadow-sm border border-gray-200 p-8'>
            <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6'>
              <svg
                className='w-8 h-8 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
            </div>
            <h2 className='text-xl font-semibold text-gray-900 mb-3'>
              No Active Negotiation
            </h2>
            <p className='text-gray-600 mb-6 text-sm leading-relaxed'>
              There is no active rate negotiation for this warehouse. Please
              wait for a shipper to initiate a request.
            </p>
            <Button
              label='Back to Warehouse'
              className='h-auto'
              onClick={() => navigate(`/warehouse/${warehouseId}`)}
            />
          </div>
        </div>
      </div>
    );
  }

  // Show signing view for contracted matches
  if (match.matchStatus === 'Reviewing T&Cs') {
    return (
      <div className='h-[calc(100vh-64px)] flex flex-col bg-gray-50'>
        <PageHeader header='Contract Signing' pages={pages} />
        <div className='flex-1 flex flex-col items-center justify-center px-10 py-6'>
          <div className='max-w-md text-center bg-white rounded-xl shadow-sm border border-gray-200 p-8'>
            <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6'>
              <svg
                className='w-8 h-8 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <h2 className='text-xl font-semibold text-gray-900 mb-3'>
              Rates Agreed
            </h2>
            <p className='text-gray-600 mb-6 text-sm leading-relaxed'>
              Both parties have agreed on the rates. Please sign the Pricing
              Schedule Addendum to complete the contract.
            </p>
            {matchSigningDetails?.isSigned === false && (
              <Button
                label='Sign Pricing Schedule'
                className='h-auto'
                onClick={() =>
                  window.open(matchSigningDetails.signingUrl, '_blank')
                }
              />
            )}
            {matchSigningDetails?.isSigned === true && (
              <div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
                <p className='text-sm text-amber-700'>
                  You have signed. Waiting for the other party to sign.
                </p>
              </div>
            )}
            {!matchSigningDetails && signingDetailsError && (
              <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
                <p className='text-sm text-red-700'>{signingDetailsError}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (match.matchStatus === 'Contracted') {
    return (
      <div className='h-[calc(100vh-64px)] flex flex-col bg-gray-50'>
        <PageHeader header='Contract' pages={pages} />
        <div className='flex-1 flex flex-col items-center justify-center px-10 py-6'>
          <div className='max-w-md text-center bg-white rounded-xl shadow-sm border border-gray-200 p-8'>
            <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6'>
              <svg
                className='w-8 h-8 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <h2 className='text-xl font-semibold text-gray-900 mb-3'>
              Contract Active
            </h2>
            <p className='text-gray-600 mb-6 text-sm leading-relaxed'>
              This contract is now active with {match.userBusinessName}.
            </p>
            <Button
              label='View Contract'
              className='h-auto'
              onClick={() =>
                window.open(match.legalDocuments?.[0]?.url, '_blank')
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <FormPage<ManagerServiceRatesFormValues>
      header='Negotiate Rates'
      pages={pages}
      methods={methods}
      steps={steps}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      submitText='Send Proposal'
      submitError={submitError}
      onDismissError={() => setSubmitError(null)}
      canSubmit={!match?.isActionRequiredByShipper}
      stepFields={{
        1: ['storagePricePerWeek', 'palletReceiptAndPutAwayPrice'],
        2: ['desiredStartDate', 'billingCycleFrequency', 'paymentTermsInDays'],
      }}
      customActions={
        match?.isActionRequiredByLsp && currentStep === 2 ? (
          <>
            <Button
              label={isDeleting ? 'Cancelling...' : 'Cancel Negotiations'}
              intent='secondary'
              size='sm'
              onClick={handleDeleteMatch}
              disabled={isSubmitting || isAccepting || isDeleting}
              type='button'
            />
            <Button
              label={isAccepting ? 'Accepting...' : 'Accept Proposed Rates'}
              intent='primary'
              size='sm'
              onClick={handleAcceptMatch}
              disabled={isSubmitting || isAccepting || isDeleting}
              type='button'
            />
          </>
        ) : undefined
      }
    >
      {/* Step 1: Service Rates */}
      {currentStep === 1 && (
        <div className='flex gap-6'>
          {/* Main Content - Left Side */}
          <div className='flex-1 min-w-0'>
            {/* Waiting for Shipper response banner */}
            {match?.isActionRequiredByShipper && (
              <div className='p-4 mb-6 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-3 shadow-sm'>
                <div className='w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0'>
                  <svg
                    className='w-5 h-5 text-amber-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                </div>
                <div>
                  <p className='text-sm font-semibold text-amber-900'>
                    Awaiting response from {match?.userBusinessName}
                  </p>
                  <p className='text-sm text-amber-700 mt-1'>
                    Your counter-proposal has been submitted. The shipper is
                    reviewing your rates.
                  </p>
                </div>
              </div>
            )}

            {/* Price requests banner */}
            {match?.isActionRequiredByLsp &&
              match?.requestedServices &&
              match.requestedServices.length > 0 && (
                <div className='p-4 mb-6 bg-blue-50 border border-blue-300 rounded-xl flex items-start gap-3 shadow-sm'>
                  <div className='w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0'>
                    <svg
                      className='w-5 h-5 text-blue-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                      />
                    </svg>
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-blue-900'>
                      Price requests from {match?.userBusinessName}
                    </p>
                    <p className='text-sm text-blue-700 mt-1'>
                      The client has requested pricing for{' '}
                      {match.requestedServices.length} service
                      {match.requestedServices.length > 1 ? 's' : ''} that you
                      haven&apos;t set. Please provide prices or mark as
                      unavailable.
                    </p>
                  </div>
                </div>
              )}

            {/* Section Header */}
            <div className='mb-6'>
              <div className='flex items-center justify-between'>
                <div>
                  <h2 className='text-xl font-semibold text-gray-900'>
                    Service Rates
                  </h2>
                  <p className='text-sm text-gray-500 mt-1'>
                    Review the shipper&apos;s proposal and counter-propose if
                    needed
                  </p>
                </div>
                <div className='text-right'>
                  <p className='text-sm text-gray-500'>Negotiating with</p>
                  <p className='text-sm font-semibold text-gray-900'>
                    {match?.userBusinessName}
                  </p>
                </div>
              </div>
            </div>

            {/* Service Rates Card */}
            <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'>
              {/* Table Header */}
              <div className='grid grid-cols-12 gap-4 py-3 px-4 bg-gray-50 border-b border-gray-200'>
                <span className='col-span-3 text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                  Service
                </span>
                <span className='col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                  Your Rate
                </span>
                <span className='col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                  Proposed
                </span>
                <span className='col-span-3 text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                  Counter
                </span>
                <span className='col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center'>
                  N/A
                </span>
              </div>

              {/* Service Rate Rows */}
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label={`Storage (per ${
                  warehouse.storageCapacityUnitOfMeasure === 'Pallets'
                    ? 'pallet-week'
                    : warehouse.storageCapacityUnitOfMeasure?.toLowerCase()
                })`}
                fieldName='storagePricePerWeek'
                warehousePrice={
                  warehouse.storageCapacityUnitOfMeasure === 'Pallets'
                    ? warehouse.palletStoragePricePerWeek
                    : warehouse.storageCapacityUnitOfMeasure === 'Square Metre'
                    ? warehouse.squareMetreStoragePricePerWeek
                    : warehouse.squareFeetStoragePricePerWeek
                }
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Receipt and putaway (per pallet)'
                fieldName='palletReceiptAndPutAwayPrice'
                warehousePrice={warehouse.palletReceiptAndPutAwayPrice}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Pallet picking and despatch (per pallet)'
                fieldName='palletPickingAndDespatchPrice'
                warehousePrice={warehouse.palletPickingAndDespatchPrice}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Pallet layer picking and despatch (per layer)'
                fieldName='palletLayerPickingAndDespatchPrice'
                warehousePrice={warehouse.palletLayerPickingAndDespatchPrice}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Case picking and despatch (per case)'
                fieldName='casePickingAndDespatchPrice'
                warehousePrice={warehouse.casePickingAndDespatchPrice}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Subsequent case picking (per case)'
                fieldName='subsequentCasePickingAndDespatchPrice'
                warehousePrice={warehouse.subsequentCasePickingAndDespatchPrice}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Item picking and despatch (per item)'
                fieldName='itemPickingAndDespatchPrice'
                warehousePrice={warehouse.itemPickingAndDespatchPrice}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Subsequent item picking (per item)'
                fieldName='subsequentItemPickingAndDespatchPrice'
                warehousePrice={warehouse.subsequentItemPickingAndDespatchPrice}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Container handling (per container)'
                fieldName='containerHandlingPrice'
                warehousePrice={warehouse.containerHandlingPrice}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Demurrage (per consignment day)'
                fieldName='demurragePricePerDay'
                warehousePrice={warehouse.demurragePricePerDay}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Pallet returns (per pallet)'
                fieldName='palletReturnsPrice'
                warehousePrice={warehouse.palletReturnsPrice}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Item returns (per item)'
                fieldName='itemReturnsPrice'
                warehousePrice={warehouse.itemReturnsPrice}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Case returns (per case)'
                fieldName='caseReturnsPrice'
                warehousePrice={warehouse.caseReturnsPrice}
              />
              <ManagerServiceRateRow
                {...serviceRateRowCommonProps}
                label='Auxiliary services (per man-hour)'
                fieldName='auxiliaryServicePricePerManHour'
                warehousePrice={warehouse.auxiliaryServicePricePerManHour}
              />
            </div>

            {/* Volume Based Discount */}
            <div className='mt-6 bg-white rounded-xl border border-gray-200 shadow-sm p-5'>
              <Controller
                name='volumeBasedDiscount'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    name={field.name}
                    label='Apply volume-based discount structure instead of per-service rates'
                    checked={field.value}
                    onChange={(e) => {
                      setValue('volumeBasedDiscount', e.target.checked, {
                        shouldValidate: true,
                      });
                    }}
                    disabled={match?.isActionRequiredByShipper}
                  />
                )}
              />

              {volumeBasedDiscount && (
                <div className='mt-6 space-y-4 pt-4 border-t border-gray-100'>
                  <h3 className='text-sm font-semibold text-gray-700 uppercase tracking-wide'>
                    Volume Discount Tiers
                  </h3>

                  {/* Tier 1 */}
                  <div className='flex items-center gap-4 p-4 bg-blue-50/50 rounded-lg border border-blue-100'>
                    <div className='w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0'>
                      <span className='text-sm font-bold text-blue-600'>1</span>
                    </div>
                    <div className='flex-1 grid grid-cols-2 gap-4'>
                      <Controller
                        name='monthlyTurnoverOne'
                        control={control}
                        render={({ field }) => (
                          <Input
                            label='Up to monthly turnover'
                            name='monthlyTurnoverOne'
                            type='number'
                            placeholder={`${currencySymbol}5,000`}
                            value={field.value?.toString() || ''}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            disabled={match?.isActionRequiredByShipper}
                          />
                        )}
                      />
                      <Controller
                        name='monthlyDiscountOne'
                        control={control}
                        render={({ field }) => (
                          <Input
                            label='Discount %'
                            name='monthlyDiscountOne'
                            type='number'
                            placeholder='5'
                            value={field.value?.toString() || ''}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            disabled={match?.isActionRequiredByShipper}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Tier 2 */}
                  <div className='flex items-center gap-4 p-4 bg-green-50/50 rounded-lg border border-green-100'>
                    <div className='w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0'>
                      <span className='text-sm font-bold text-green-600'>
                        2
                      </span>
                    </div>
                    <div className='flex-1 grid grid-cols-2 gap-4'>
                      <Controller
                        name='monthlyTurnoverTwo'
                        control={control}
                        render={({ field }) => (
                          <Input
                            label='Up to monthly turnover'
                            name='monthlyTurnoverTwo'
                            type='number'
                            placeholder={`${currencySymbol}15,000`}
                            value={field.value?.toString() || ''}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            disabled={match?.isActionRequiredByShipper}
                          />
                        )}
                      />
                      <Controller
                        name='monthlyDiscountTwo'
                        control={control}
                        render={({ field }) => (
                          <Input
                            label='Discount %'
                            name='monthlyDiscountTwo'
                            type='number'
                            placeholder='10'
                            value={field.value?.toString() || ''}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            disabled={match?.isActionRequiredByShipper}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Tier 3 */}
                  <div className='flex items-center gap-4 p-4 bg-purple-50/50 rounded-lg border border-purple-100'>
                    <div className='w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0'>
                      <span className='text-sm font-bold text-purple-600'>
                        3
                      </span>
                    </div>
                    <div className='flex-1 grid grid-cols-2 gap-4'>
                      <div className='flex items-end pb-2'>
                        <span className='text-sm text-gray-600 font-medium'>
                          Above tier 2
                        </span>
                      </div>
                      <Controller
                        name='monthlyDiscountThree'
                        control={control}
                        render={({ field }) => (
                          <Input
                            label='Discount %'
                            name='monthlyDiscountThree'
                            type='number'
                            placeholder='15'
                            value={field.value?.toString() || ''}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            disabled={match?.isActionRequiredByShipper}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Disclaimer (Right Side) */}
          <div className='hidden lg:block w-80 flex-shrink-0'>
            <div className='sticky top-6 space-y-4'>
              {/* Shipper Requirements */}
              <div className='p-5 bg-blue-50 rounded-xl border border-blue-200'>
                <h3 className='text-sm font-semibold text-blue-900 mb-3'>
                  Shipper Requirements
                </h3>
                <div className='space-y-2 text-sm text-blue-800'>
                  <div className='flex justify-between'>
                    <span>Storage needed</span>
                    <span className='font-medium'>
                      {match?.averageStorageQuantityPerWeek}{' '}
                      {warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}
                      /week
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Duration</span>
                    <span className='font-medium'>
                      {match?.estimatedDurationInMonths} months
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Inbound</span>
                    <span className='font-medium'>
                      {match?.averageNumberOfInboundShipmentsPerWeek}/week
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Outbound</span>
                    <span className='font-medium'>
                      {match?.averageNumberOfOutboundShipmentsPerWeek}/week
                    </span>
                  </div>
                </div>
              </div>

              {/* Pricing Info */}
              <div className='p-5 bg-gray-50 rounded-xl border border-gray-200'>
                <div className='flex items-start gap-3'>
                  <svg
                    className='w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                  <div>
                    <p className='text-sm font-medium text-gray-700 mb-3'>
                      Pricing Information
                    </p>
                    <p className='text-xs text-gray-500 mb-3'>
                      All prices are exclusive of VAT and FLOX transaction fees.
                    </p>
                    {match?.floxTransactionFees && (
                      <div className='space-y-2 text-xs text-gray-500'>
                        <div className='flex justify-between py-1.5 border-b border-gray-200'>
                          <span>Storage</span>
                          <span className='font-medium text-gray-700'>
                            {match.floxTransactionFees.storageFeePercentage}%
                          </span>
                        </div>
                        <div className='flex justify-between py-1.5 border-b border-gray-200'>
                          <span>Handling</span>
                          <span className='font-medium text-gray-700'>
                            {match.floxTransactionFees.handlingFeePercentage}%
                          </span>
                        </div>
                        <div className='flex justify-between py-1.5 border-b border-gray-200'>
                          <span>Other Services</span>
                          <span className='font-medium text-gray-700'>
                            {match.floxTransactionFees.otherServicesPercentage}%
                          </span>
                        </div>
                        <div className='flex justify-between py-1.5 border-b border-gray-200'>
                          <span>Haulage</span>
                          <span className='font-medium text-gray-700'>
                            {match.floxTransactionFees.haulageFeePercentage}%
                          </span>
                        </div>
                        <div className='flex justify-between py-1.5'>
                          <span>Distribution</span>
                          <span className='font-medium text-gray-700'>
                            {
                              match.floxTransactionFees
                                .distributionFeePercentage
                            }
                            %
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Terms & Commitment */}
      {currentStep === 2 && (
        <div className='max-w-4xl'>
          {/* Waiting for Shipper response banner */}
          {match?.isActionRequiredByShipper && (
            <div className='p-4 mb-6 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-3 shadow-sm'>
              <div className='w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0'>
                <svg
                  className='w-5 h-5 text-amber-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
              </div>
              <div>
                <p className='text-sm font-semibold text-amber-900'>
                  Awaiting response from {match?.userBusinessName}
                </p>
                <p className='text-sm text-amber-700 mt-1'>
                  Your counter-proposal has been submitted. The fields below are
                  read-only until the shipper responds.
                </p>
              </div>
            </div>
          )}

          {/* Section Header */}
          <div className='mb-6'>
            <h2 className='text-xl font-semibold text-gray-900'>
              Terms & Commitment
            </h2>
            <p className='text-sm text-gray-500 mt-1'>
              Review and adjust the billing terms for this contract
            </p>
          </div>

          {/* Payment Terms Card */}
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6'>
            <h3 className='text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2'>
              <svg
                className='w-4 h-4 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z'
                />
              </svg>
              Payment Terms
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Controller
                name='billingCycleFrequency'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Billing Cycle'
                    options={BILLING_CYCLE_OPTIONS}
                    selected={
                      BILLING_CYCLE_OPTIONS.find(
                        (o) => o.value === field.value
                      ) || BILLING_CYCLE_OPTIONS[2]
                    }
                    setSelected={(opt) => field.onChange(opt.value)}
                  />
                )}
              />
              <Controller
                name='paymentTermsInDays'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Payment Terms'
                    options={PAYMENT_TERMS_OPTIONS}
                    selected={
                      PAYMENT_TERMS_OPTIONS.find(
                        (o) => o.value === field.value
                      ) || PAYMENT_TERMS_OPTIONS[2]
                    }
                    setSelected={(opt) => field.onChange(opt.value)}
                  />
                )}
              />
            </div>
          </div>

          {/* Contract Duration Card */}
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6'>
            <h3 className='text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2'>
              <svg
                className='w-4 h-4 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                />
              </svg>
              Contract Details
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Controller
                name='desiredStartDate'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Datepicker
                    label='Desired Start Date'
                    name='desiredStartDate'
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    startDate={dayjs().format('YYYY-MM-DD')}
                    disabled={match?.isActionRequiredByShipper}
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />
              <div>
                <p className='block text-sm font-medium text-gray-700 mb-1'>
                  Estimated Duration
                </p>
                <p className='text-lg font-semibold text-gray-900'>
                  {match?.estimatedDurationInMonths} months
                </p>
              </div>
            </div>
          </div>

          {/* Shipper Commitment Summary */}
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6'>
            <h3 className='text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2'>
              <svg
                className='w-4 h-4 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
                />
              </svg>
              Shipper Commitment
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              <div>
                <p className='block text-sm text-gray-500 mb-1'>
                  Storage (
                  {warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}
                  /week)
                </p>
                <p className='text-lg font-semibold text-gray-900'>
                  {match?.averageStorageQuantityPerWeek}
                </p>
              </div>
              <div>
                <p className='block text-sm text-gray-500 mb-1'>
                  Inbound (per week)
                </p>
                <p className='text-lg font-semibold text-gray-900'>
                  {match?.averageNumberOfInboundShipmentsPerWeek}
                </p>
              </div>
              <div>
                <p className='block text-sm text-gray-500 mb-1'>
                  Outbound (per week)
                </p>
                <p className='text-lg font-semibold text-gray-900'>
                  {match?.averageNumberOfOutboundShipmentsPerWeek}
                </p>
              </div>
            </div>
          </div>

          {/* Comments Card */}
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-6'>
            <h3 className='text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2'>
              <svg
                className='w-4 h-4 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z'
                />
              </svg>
              Comments
            </h3>

            {/* Previous Comments */}
            <CommentThread
              comments={match?.comments ?? []}
              currentBusinessName={user?.businessName ?? ''}
            />

            <Controller
              name='comment'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Add a comment'
                  name='comment'
                  placeholder='Add any comments or notes for the shipper...'
                  value={field.value}
                  onChange={field.onChange}
                  disabled={match?.isActionRequiredByShipper}
                />
              )}
            />
          </div>

          <p className='mt-6 text-xs text-gray-400 text-center'>
            * Surcharges may apply for rush orders, peak season, delays,
            cancellations, late payments, and extraordinary service requests.
          </p>
        </div>
      )}
    </FormPage>
  );
}
